import { Capacitor } from "@capacitor/core";
import { Geolocation as CapacitorGeolocation } from "@capacitor/geolocation";
import { useState } from "react";
import { storageKey } from "../configs/config";
import { safeLog } from "../error-logger";
import api, { UpdateProfileLocationData } from "../services/api";

export type Location = {
  lat: number;
  lon: number;
};

export const useGeolocation = () => {
  const [permission, setPermissionState] = useState<PermissionState>(
    (localStorage.getItem(storageKey.geoLocPerm) as PermissionState) ?? "prompt"
  );
  const setPermission = (value: PermissionState) => {
    setPermissionState(value);
    localStorage.setItem(storageKey.geoLocPerm, value);
  };

  const [location, setLocation] = useState<Location | null>(() => {
    const stored = sessionStorage.getItem(storageKey.userLocation);
    return stored ? (JSON.parse(stored) as Location) : null;
  });

  // For IOS emulator debugging (geolocation does not allow http with "vite --host")
  // const [permission, setPermissionState] = useState<PermissionState>("granted");
  // const [location, setLocation] = useState<Location>({ lat: 50.650609737939675, lon: 3.0414721715096675 });

  const [loading, setLoading] = useState(false);

  const getLocation = async () => {
    console.log("getLocation RUNNING");
    setLoading(true);

    if (Capacitor.isNativePlatform()) {
      // --- Native mobile: Capacitor Geolocation ---
      try {
        // 1️⃣ Check geolocation permission FIRST
        // const permStatus = await CapacitorGeolocation.checkPermissions();

        // if (permStatus.location === "denied") {
        //   setPermission("denied");
        //   alert("Location access is denied. Please enable it in your settings to continue.");
        //   return;
        // }

        // // 2️⃣ Request permission explicitly (Android/iOS)
        // const permRequest = await CapacitorGeolocation.requestPermissions();

        // if (permRequest.location !== "granted") {
        //   setPermission("denied");
        //   alert("Location access is denied. Please enable it in your settings to continue.");
        //   return;
        // }

        // 3️⃣ Only now call getCurrentPosition()
        const position = await CapacitorGeolocation.getCurrentPosition({ timeout: 30000, enableHighAccuracy: true });
        const { latitude, longitude } = position.coords;

        const loc = { lat: latitude, lon: longitude };
        setLocation(loc);
        sessionStorage.setItem(storageKey.userLocation, JSON.stringify(loc));
        console.log("Location set in SS");
        handleUpdateLocation({ coordinates: [longitude, latitude] });
        // Lastly change permission to avoid the swipper component to init before location is set in session storage
        setPermission("granted");
        console.log("permission set");
      } catch (err) {
        console.error("Capacitor Geolocation Error:", err);
        if (err instanceof Error) {
          safeLog({ type: "geolocation", message: err.message });
          if (err.message?.includes("permission")) {
            setPermission("denied");
            alert("Location access is denied. Please enable it in your settings to continue.");
          }
        }
      } finally {
        setLoading(false);
      }
    } else {
      // --- Web: navigator.geolocation ---
      if (!navigator.geolocation) {
        alert("La géolocalisation n'est pas supportée par ce navigateur.");
        setLoading(false);
        return;
      }

      // const permissionStatus = await navigator.permissions
      //   .query({ name: "geolocation" as PermissionName })
      //   .catch(() => null);

      // console.log("permissionStatus", permissionStatus);

      // if (permissionStatus?.state === "denied") {
      //   alert("La permission de géolocalisation est refusée dans le navigateur.");
      //   setPermission("denied");
      //   return;
      // }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          // if (permission !== "granted") setPermission("granted");

          const loc = { lat: latitude, lon: longitude };
          console.log(position);
          setLocation(loc);
          // Necessary when getCurrentPosition is delayed by the device after too many requests.
          sessionStorage.setItem(storageKey.userLocation, JSON.stringify(loc));
          console.log("Location set in SS");
          handleUpdateLocation({ coordinates: [longitude, latitude] });
          // Lastly change permission to avoid the swipper component to init before location is set in session storage
          setPermission("granted");
          console.log("permission set");
          setLoading(false);
        },
        (err) => {
          console.error("Browser Geolocation Error:", err);
          safeLog({ type: "geolocation", message: err.message, code: err.code });
          if (err.code === err.PERMISSION_DENIED) {
            setPermission("denied");
            alert("Location access is denied. Please enable it in your browser settings to continue.");
          }
          setLoading(false);
        }
      );
    }
  };

  const handleUpdateLocation = async (data: UpdateProfileLocationData) => {
    try {
      await api.updateLocation(data);
    } catch (err) {
      console.log("Error updating location", err);
      alert("Error updating location");
    }
  };

  // const debouncedUpdateLocation = useCallback(
  //   lodash.debounce(async (data: UpdateProfileLocationData) => {
  //     console.log("Debounced function RUNNING", data);
  //     try {
  //       await api.updateLocation(data);
  //     } catch (err) {
  //       console.log("Error updating location", err);
  //       alert("Error updating location");
  //     }
  //   }, 300 * 1000),
  //   []
  // );

  // const trackLocation = () => {
  //   if (!navigator.geolocation) {
  //     setLocation((prev) => ({ ...prev, error: "Geolocation not supported" }));
  //     return;
  //   }

  //   const onSuccess = (position: GeolocationPosition) => {
  //     const { latitude, longitude } = position.coords;
  //     setLocation({ lat: latitude, lon: longitude, error: null });
  //     debouncedUpdateLocation({ coordinates: [latitude, longitude] });
  //   };

  //   const onError = (error: GeolocationPositionError) => {
  //     setLocation((prev) => ({ ...prev, error: error.message }));
  //   };

  //   const watchId = navigator.geolocation.watchPosition(onSuccess, onError);

  //   return () => navigator.geolocation.clearWatch(watchId);
  // };

  return { permission, location, loading, getLocation };
};
